/**
 * KNOWLEDGE — LIFT AND SHIFT (KNOWLEDGE.md §8 K4 / §13a): a network's knowledge as ONE file, SAVED in the app first
 * (listed below, reusable by any network of the workspace — "Import here" — never lost); Download is the second
 * option. Export it (its rules, trained
 * answers, skills, procedures, settings — and its documents only when you include them; fact values, track records
 * and lessons never leave the workspace) and import a file another workspace exported: a preview first, then for
 * real (rules arrive as suggestions to accept in the Rules tab). The network's creator or a workspace admin acts —
 * the server enforces it. Backend: PUT /networks/{id} ``{knowledge: {action: export|import}}``.
 *
 * KEPT FROM ROOMS (K5, §14): what people in this network's rooms kept as knowledge (a message → a document, a
 * procedure, a trained answer) waits here — Accept writes it where it belongs, Decline drops it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { confirmDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';
import { carrySummary } from './CompanySharingSection.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
export const MAX_IMPORT_BYTES = 4 * 1024 * 1024;     // = networks_impl.MAX_KNOWLEDGE_IMPORT
const KIND_WORDS = { 'fact-value': 'facts', 'track-record': 'track records', lesson: 'lessons', document: 'documents' };

/** PURE — what an export left behind, in plain words ('' when nothing). */
export function withheldWords(w) {
  const parts = Object.entries(w || {}).filter(([, n]) => n).map(([k, n]) => `${n} ${KIND_WORDS[k] || k}`);
  return parts.length ? `Stayed in this workspace: ${parts.join(', ')}.` : '';
}

async function put(networkId, knowledge) {
  const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(networkId)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ knowledge }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
  return body.knowledge || body;
}

const toBase64 = (buf) => {
  let s = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

const KEPT_KIND = { document: 'Document', procedure: 'Procedure', qa: 'Trained answer' };

/** PURE — one kept item in a line (its name / question + where it came from). */
export function keptLine(p) {
  const b = p?.body || {};
  const what = p?.kind === 'qa' ? b.question : p?.kind === 'procedure' ? (b.title || b.id) : b.name;
  return `${KEPT_KIND[p?.kind] || p?.kind}: ${what || '—'}${p?.room ? ` · from ${p.room}` : ''}${p?.by ? ` · ${p.by}` : ''}`;
}

export default function KnowledgeSection({ network }) {
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [docs, setDocs] = useState(false);
  const [kept, setKept] = useState([]);
  const [saved, setSaved] = useState([]);
  const file = useRef(null);
  const canAct = !!network?.canChangeAccess;              // same gate as every sibling section (fail closed)
  const loadKept = useCallback(() => {
    if (!network?.networkId || !canAct) return;
    put(network.networkId, { action: 'proposals' }).then((r) => setKept(r.proposals || [])).catch(() => setKept([]));
  }, [network?.networkId, canAct]);
  const loadSaved = useCallback(() => {
    if (!network?.networkId || !canAct) return;
    put(network.networkId, { action: 'exports' }).then((r) => setSaved(r.exports || [])).catch(() => setSaved([]));
  }, [network?.networkId, canAct]);
  useEffect(() => { loadKept(); loadSaved(); }, [loadKept, loadSaved]);
  if (!network?.networkId) return null;
  const decide = (p, action) => run(`${action}:${p.id}`, async () => {
    const r = await put(network.networkId, { action, id: p.id });
    setKept(r.proposals || []);
    setMsg(action === 'accept' ? `Kept: ${keptLine(p)}` : 'Declined.');
  });

  const run = async (key, fn) => {
    setBusy(key); setErr(''); setMsg('');
    try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const download = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'knowledge.knpack';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const doExport = () => run('export', async () => {
    const out = await put(network.networkId, { action: 'export', includeDocuments: docs });
    setMsg(['Saved in the app — see Saved exports.', withheldWords(out.manifest?.withheld)].filter(Boolean).join(' '));
    loadSaved();
  });
  const doDownload = (e) => run(`dl:${e.packId}`, async () => {
    const out = await put(network.networkId, { action: 'download', networkId: e.networkId, packId: e.packId });
    download(out.downloadUrl, out.fileName);
  });
  const doDelete = (e) => run(`del:${e.packId}`, async () => {
    if (!(await confirmDialog({ title: 'Delete this export?', message: `${e.title} · ${String(e.at).slice(0, 10)} — the saved file goes.`,
      confirmLabel: 'Delete', danger: true }))) return;
    const out = await put(network.networkId, { action: 'deleteexport', packId: e.packId });
    setSaved(out.exports || []);
  });
  const doImportSaved = (e) => run(`imp:${e.packId}`, async () => {
    const src = { networkId: e.networkId, packId: e.packId };
    const preview = await put(network.networkId, { action: 'import', fromExport: src, dryRun: true });
    if (!(await confirmDialog({ title: `Import ${e.title}'s knowledge here?`,
      message: `${carrySummary(preview)} Rules arrive as suggestions — accept them in the Rules tab.`,
      confirmLabel: 'Import' }))) return;
    const done = await put(network.networkId, { action: 'import', fromExport: src });
    setMsg(`Imported: ${carrySummary(done)}`);
  });
  const doImport = (f) => run('import', async () => {
    if (f.size > MAX_IMPORT_BYTES) throw new Error('A knowledge file is at most 4 MB.');   // the server's cap, said early
    const pack = toBase64(await f.arrayBuffer());
    const preview = await put(network.networkId, { action: 'import', pack, dryRun: true });
    const from = preview.from?.title ? ` from ${preview.from.title}` : '';
    if (!(await confirmDialog({ title: `Import knowledge${from}?`,
      message: `${carrySummary(preview)} Rules arrive as suggestions — accept them in the Rules tab.`,
      confirmLabel: 'Import' }))) return;
    const done = await put(network.networkId, { action: 'import', pack });
    setMsg(`Imported: ${carrySummary(done)}`);
  });
  const spin = (key, label, working) => (busy === key
    ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" aria-hidden />{working}</span>
    : label);

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-3" data-testid="knowledge-section">
      <h3 className="text-sm font-semibold text-gray-800">Knowledge</h3>
      <p className="text-xs text-gray-500">
        Move what this network knows — its rules, trained answers, skills, procedures and settings — as one file. Exports
        are saved here (any network of this workspace can import one); download one to take it to another workspace.
        Facts, track records and lessons stay in this workspace.
      </p>
      {canAct ? (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button type="button" disabled={!!busy} onClick={doExport} data-testid="knowledge-export"
            className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>
            {spin('export', 'Export knowledge', 'Exporting…')}
          </button>
          <label className="inline-flex items-center gap-1 text-gray-600">
            <input type="checkbox" checked={docs} onChange={(e) => setDocs(e.target.checked)} data-testid="knowledge-include-docs" />
            include its documents (they leave this workspace)
          </label>
          <button type="button" disabled={!!busy} onClick={() => file.current?.click()} data-testid="knowledge-import"
            className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>
            {spin('import', 'Import knowledge…', 'Reading…')}
          </button>
          <input ref={file} type="file" accept=".knpack" className="hidden" data-testid="knowledge-file"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) doImport(f); }} />
        </div>
      ) : (
        <p className="text-xs text-gray-500">Only this network&apos;s creator or a workspace admin moves its knowledge.</p>
      )}
      {canAct && saved.length ? (
        <div data-testid="knowledge-saved">
          <p className="text-xs font-medium text-gray-700">Saved exports</p>
          <ul className="mt-1 divide-y divide-gray-100 text-xs">
            {saved.map((e) => {
              const n = Object.values(e.kinds || {}).reduce((a, b) => a + b, 0);
              const own = e.networkId === network.networkId;
              return (
                <li key={`${e.networkId}/${e.packId}`} className="flex items-center gap-2 py-1.5" data-testid={`knowledge-saved-${e.packId}`}>
                  <span className="min-w-0 flex-1 truncate text-gray-700">
                    {e.title} · {String(e.at).slice(0, 10)} · {n} item{n === 1 ? '' : 's'}{e.documents ? ' · with documents' : ''}
                  </span>
                  {!own ? (
                    <button type="button" disabled={!!busy} onClick={() => doImportSaved(e)} data-testid={`knowledge-saved-import-${e.packId}`}
                      className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>
                      {spin(`imp:${e.packId}`, 'Import here', 'Reading…')}</button>
                  ) : null}
                  <button type="button" disabled={!!busy} onClick={() => doDownload(e)} data-testid={`knowledge-saved-download-${e.packId}`}
                    className={`${btn} text-gray-600 hover:bg-gray-100 disabled:opacity-50`}>{spin(`dl:${e.packId}`, 'Download', '…')}</button>
                  {own ? (
                    <button type="button" disabled={!!busy} onClick={() => doDelete(e)} data-testid={`knowledge-saved-delete-${e.packId}`}
                      className={`${btn} text-rose-700 hover:bg-rose-50 disabled:opacity-50`}>Delete</button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {canAct && kept.length ? (
        <div data-testid="knowledge-kept">
          <p className="text-xs font-medium text-gray-700">Kept from rooms — waiting for you ({kept.length})</p>
          <ul className="mt-1 divide-y divide-gray-100 text-xs">
            {kept.map((p) => (
              <li key={p.id} className="flex items-center gap-2 py-1.5" data-testid={`knowledge-kept-${p.id}`}>
                <span className="min-w-0 flex-1 truncate text-gray-700" title={keptLine(p)}>{keptLine(p)}</span>
                <button type="button" disabled={!!busy} onClick={() => decide(p, 'accept')} data-testid={`knowledge-accept-${p.id}`}
                  className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}>
                  {spin(`accept:${p.id}`, 'Accept', 'Keeping…')}</button>
                <button type="button" disabled={!!busy} onClick={() => decide(p, 'decline')} data-testid={`knowledge-decline-${p.id}`}
                  className={`${btn} text-gray-600 hover:bg-gray-100 disabled:opacity-50`}>Decline</button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {msg ? <p className="text-xs text-gray-600" data-testid="knowledge-msg">{msg}</p> : null}
      {err ? <p className="text-xs text-rose-600">{err}</p> : null}
    </section>
  );
}
